import { computed, ref, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import type { PokerlyWs } from '@/services/pokerly-ws/index.js';
import type { RoundStatus, RoundVotingScaleSnapshot, SessionStatus, VotingScaleSize } from '@pokerly/contracts';

interface RoundStateType {
    ACTIVE: 'active';
    CLOSED: 'closed';
}

export type RoundState = RoundStateType[keyof RoundStateType];

export const RoundStates: RoundStateType = {
    ACTIVE: 'active',
    CLOSED: 'closed',
};

export const useSessionStateStore = defineStore('session-state', () => {
    const room = ref<string>();
    const sessionId = ref<string>();
    const sessionStatus = ref<SessionStatus>('created');
    const votingScaleId = ref<string | null>(null);
    const votingScaleSize = ref<VotingScaleSize | null>(null);
    const roundId = ref<string>();
    const roundReplayId = ref<string>();
    const roundState = ref<RoundState>(RoundStates.ACTIVE);
    const roundVotingScale = ref<RoundVotingScaleSnapshot | null>(null);
    const estimationOptions = computed(() => roundVotingScale.value?.options.map((option) => option.value) ?? []);

    const pokerly = shallowRef<PokerlyWs | null>(null);

    function setWs(ws: PokerlyWs) {
        pokerly.value = ws;
    }

    function isInitialized(): boolean {
        return pokerly.value !== null;
    }

    function getWs(): PokerlyWs {
        if (!pokerly.value) {
            throw new Error('Session store not initialized. Call init() first.');
        }
        return pokerly.value;
    }

    function setRoundState(status: RoundState) {
        if (roundState.value === status) return;
        roundState.value = status;
    }

    function setSessionStatus(status: SessionStatus) {
        sessionStatus.value = status;
    }

    function setRoundIds(newRoundId?: string | null, newRoundReplayId?: string | null) {
        roundId.value = newRoundId ?? undefined;
        roundReplayId.value = newRoundReplayId ?? undefined;
    }

    function setVotingScaleAssignment(newVotingScaleId: string | null, newVotingScaleSize: VotingScaleSize | null) {
        votingScaleId.value = newVotingScaleId;
        votingScaleSize.value = newVotingScaleSize;
    }

    function setRoundVotingScale(snapshot: RoundVotingScaleSnapshot | null) {
        roundVotingScale.value = snapshot;
    }

    function setSessionId(id: string) {
        sessionId.value = id;
    }

    function setRoom(value: string) {
        if (room.value === value) return;
        room.value = value;
        if (isInitialized()) {
            getWs().reconnect();
        }
    }

    const isRoundClosed = computed(() => roundState.value === RoundStates.CLOSED);
    const isSessionCreated = computed(() => sessionStatus.value === 'created');
    const isSessionActive = computed(() => sessionStatus.value === 'active');
    const canVote = computed(() => isSessionActive.value && !!roundId.value && !!roundReplayId.value);

    async function closeRound() {
        if (!canVote.value) return;
        setRoundState(RoundStates.CLOSED);
        await getWs().closeRound();
    }

    async function finishRound() {
        if (!canVote.value) return;
        const { roundReplayId: newRoundReplayId, roundId: newRoundId } = await getWs().finishRound();
        setRoundIds(newRoundId, newRoundReplayId);
        setRoundState(RoundStates.ACTIVE);
    }

    async function replayRound() {
        if (!canVote.value) return;
        const { roundReplayId: newRoundReplayId } = await getWs().replayRound();
        roundReplayId.value = newRoundReplayId;
        setRoundState(RoundStates.ACTIVE);
    }

    async function assignVotingScale(newVotingScaleId: string, newVotingScaleSize: VotingScaleSize) {
        if (!sessionId.value) {
            throw new Error('Session ID is required before assigning a voting scale.');
        }
        return await getWs().assignVotingScale(newVotingScaleId, newVotingScaleSize);
    }

    function startRound() {
        if (!canVote.value) return;
        setRoundState(RoundStates.ACTIVE);
        getWs().startRound();
    }

    function applyRoundStatus(status: RoundStatus | null) {
        if (!status) {
            setRoundState(RoundStates.ACTIVE);
            return;
        }
        setRoundState(status === 'open' ? RoundStates.ACTIVE : RoundStates.CLOSED);
    }

    function $reset() {
        room.value = undefined;
        sessionId.value = undefined;
        sessionStatus.value = 'created';
        votingScaleId.value = null;
        votingScaleSize.value = null;
        roundId.value = undefined;
        roundReplayId.value = undefined;
        roundState.value = RoundStates.ACTIVE;
        roundVotingScale.value = null;
    }

    return {
        room,
        sessionId,
        sessionStatus,
        votingScaleId,
        votingScaleSize,
        roundId,
        roundReplayId,
        roundState,
        roundVotingScale,
        estimationOptions,
        isRoundClosed,
        isSessionCreated,
        isSessionActive,
        canVote,
        setWs,
        isInitialized,
        getWs,
        setRoundState,
        setSessionStatus,
        setRoundIds,
        setVotingScaleAssignment,
        setRoundVotingScale,
        setSessionId,
        setRoom,
        applyRoundStatus,
        closeRound,
        finishRound,
        replayRound,
        assignVotingScale,
        startRound,
        $reset,
    };
});
